import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  generateService,
  StrategyGenerationResult,
  TweetCandidate,
} from '../services/generateService';
import { storageService } from '../services/storageService';
import { draftService } from '../services/draftService';
import { whisperService } from '../services/whisperService';
import { compressAudio } from '../utils/audioCompression';
import { TweetEditor } from './TweetEditor';
import { TemplateSelector } from './TemplateSelector';
import { WeightAdjuster } from './WeightAdjuster';
import { ScoreBreakdown } from './ScoreBreakdown';
import { cn } from '../lib/cn';
import { useT } from '../lib/i18n';
import { handlePostToX } from '../utils/handlePostToX';
import { useToast } from './Toast';
import { QuickActions } from './QuickActions';
import { TweetSettings } from './TweetSettings';
import { TweetSettingsValues, loadTweetSettingsFromStorage } from '../lib/tweetSettings';

const STORAGE_KEY_INPUT = 'braindump_input';
const STORAGE_KEY_CANDIDATES = 'braindump_candidates';
const STORAGE_KEY_GENERATE_COUNT = 'braindump_generate_count';
const STORAGE_KEY_SHOW_ALL = 'braindump_show_all';
const STORAGE_KEY_STRATEGY_RESULT = 'braindump_strategy_result';

export const BrainDump: React.FC = () => {
  const { t } = useT();
  const { toast } = useToast();
  const [textInput, setTextInput] = useState<string>(() => localStorage.getItem(STORAGE_KEY_INPUT) || '');
  const [status, setStatus] = useState<'idle' | 'generating' | 'complete' | 'error'>('idle');
  const [tweetCandidates, setTweetCandidates] = useState<TweetCandidate[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEY_CANDIDATES);
    return saved ? JSON.parse(saved) : [];
  });
  const [generateCount, setGenerateCount] = useState<number>(() => {
    const saved = localStorage.getItem(STORAGE_KEY_GENERATE_COUNT);
    return saved ? parseInt(saved, 10) : 20;
  });
  const [showAll, setShowAll] = useState<boolean>(() => localStorage.getItem(STORAGE_KEY_SHOW_ALL) === 'true');
  const [selectedTemplates, setSelectedTemplates] = useState<string[]>([]);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showTweetSettings, setShowTweetSettings] = useState(false);
  const [expandedScoreIndex, setExpandedScoreIndex] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const [tweetSettings, setTweetSettings] = useState<TweetSettingsValues>(() => loadTweetSettingsFromStorage());
  const [strategyResult, setStrategyResult] = useState<StrategyGenerationResult | null>(() => {
    const saved = localStorage.getItem(STORAGE_KEY_STRATEGY_RESULT);
    return saved ? JSON.parse(saved) : null;
  });
  const [strategyStatus, setStrategyStatus] = useState<'idle' | 'generating' | 'complete' | 'error'>('idle');
  const [strategyError, setStrategyError] = useState<string | null>(null);
  const [showThoughtLog, setShowThoughtLog] = useState(false);
  const [copiedSection, setCopiedSection] = useState<string | null>(null);

  // Voice recording state
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const MAX_RECORDING_DURATION = 60; // seconds

  const stopRecordingCleanup = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    setIsRecording(false);
    setRecordingTime(0);
  }, []);

  const handleStartRecording = async () => {
    if (isRecording || isTranscribing) return;
    if (!navigator.mediaDevices?.getUserMedia) {
      toast(t('voiceRecorder.errors.notSupported'), 'error');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
      streamRef.current = stream;
      const recorder = new MediaRecorder(stream);
      mediaRecorderRef.current = recorder;
      audioChunksRef.current = [];

      recorder.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      recorder.onstop = async () => {
        stopRecordingCleanup();
        const rawBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        if (rawBlob.size === 0) return;

        setIsTranscribing(true);
        try {
          const compressed = await compressAudio(rawBlob);
          const result = await whisperService.transcribe(compressed);
          if (result.text) {
            setTextInput(prev => prev ? `${prev}\n${result.text}` : result.text);
            toast(t('brainDump.voiceAdded'), 'success');
          }
        } catch (err) {
          toast(err instanceof Error ? err.message : 'Transcription failed', 'error');
        } finally {
          setIsTranscribing(false);
        }
      };

      recorder.start();
      setIsRecording(true);
      setRecordingTime(0);
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          if (prev + 1 >= MAX_RECORDING_DURATION) {
            mediaRecorderRef.current?.stop();
          }
          return prev + 1;
        });
      }, 1000);
    } catch (err: any) {
      if (err?.name === 'NotAllowedError') {
        toast(t('voiceRecorder.errors.accessDenied'), 'error');
      } else {
        toast(t('voiceRecorder.errors.startFailed'), 'error');
      }
    }
  };

  const handleStopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
    }
  };

  // Cleanup on unmount
  useEffect(() => () => { stopRecordingCleanup(); }, [stopRecordingCleanup]);

  const mainProgress = status === 'generating' ? 70 : status === 'complete' ? 100 : 0;
  const strategyProgress = strategyStatus === 'generating' ? 70 : strategyStatus === 'complete' ? 100 : 0;

  useEffect(() => { localStorage.setItem(STORAGE_KEY_INPUT, textInput); }, [textInput]);
  useEffect(() => { localStorage.setItem(STORAGE_KEY_CANDIDATES, JSON.stringify(tweetCandidates)); }, [tweetCandidates]);
  useEffect(() => { localStorage.setItem(STORAGE_KEY_GENERATE_COUNT, generateCount.toString()); }, [generateCount]);
  useEffect(() => { localStorage.setItem(STORAGE_KEY_SHOW_ALL, showAll.toString()); }, [showAll]);
  useEffect(() => { if (strategyResult) localStorage.setItem(STORAGE_KEY_STRATEGY_RESULT, JSON.stringify(strategyResult)); }, [strategyResult]);

  const handleGenerate = async () => {
    if (!textInput.trim()) return;
    try {
      setStatus('generating');
      setError(null);
      const styleModel = storageService.getStyleModel();
      const buzzModel = storageService.getBuzzModel();
      const generateResult = await generateService.generateTweets(textInput, styleModel?.systemPrompt || '', buzzModel?.insights || [], true, generateCount, showAll);
      setTweetCandidates(generateResult.candidates);
      if (generateResult.candidates.length > 0) {
        storageService.addHistoryItem({ type: 'tweet', content: generateResult.candidates[0].text, score: generateResult.candidates[0].score, metadata: { inputText: textInput.slice(0, 200), breakdown: generateResult.candidates[0].breakdown } });
      }
      setStatus('complete');
    } catch (err) {
      setStatus('error');
      setError(err instanceof Error ? err.message : 'Generation failed');
    }
  };

  const handleStrategyGenerate = async () => {
    if (!textInput.trim()) return;
    try {
      setStrategyStatus('generating');
      setStrategyError(null);
      const styleModel = storageService.getStyleModel();
      const buzzModel = storageService.getBuzzModel();
      const result = await generateService.generateStrategyTweets(textInput, styleModel?.systemPrompt || '', tweetSettings, buzzModel?.insights || []);
      setStrategyResult(result.strategy);
      setStrategyStatus('complete');
      setShowThoughtLog(true);
      if (result.strategy?.tweet) {
        storageService.addHistoryItem({ type: 'tweet', content: result.strategy.tweet, metadata: { inputText: textInput.slice(0, 200), mode: 'strategy' } });
      }
    } catch (err) {
      setStrategyStatus('error');
      setStrategyError(err instanceof Error ? err.message : 'Strategy generation failed');
    }
  };

  const handleCopyTweet = (text: string, index: number) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 1500);
  };

  const handleCopySection = (text: string, key: string) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedSection(key);
    setTimeout(() => setCopiedSection(null), 1200);
  };

  const handleEditSave = (index: number, newText: string) => {
    const updated = [...tweetCandidates];
    updated[index] = { ...updated[index], text: newText };
    setTweetCandidates(updated);
    setEditingIndex(null);
  };

  const isDisabled = !textInput.trim() || status === 'generating';
  const isStrategyDisabled = !textInput.trim() || strategyStatus === 'generating';

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[880px] mx-auto">
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold mb-1.5 text-text-primary tracking-tight">
          {t('brainDump.title')}
        </h1>
        <p className="text-text-muted text-sm sm:text-[15px] leading-relaxed">
          {t('brainDump.subtitle')}
        </p>
      </div>

      {/* Settings Bar */}
      <div className="mb-4 flex flex-wrap gap-2.5 items-center text-sm">
        <div className="flex items-center gap-2">
          <label className="text-text-secondary text-[13px]">{t('brainDump.countLabel')}</label>
          <select
            value={generateCount}
            onChange={(e) => setGenerateCount(parseInt(e.target.value, 10))}
            className="px-2.5 py-1.5 bg-surface-2 border border-border rounded-lg text-text-primary text-[13px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/50 cursor-pointer"
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={15}>15</option>
            <option value={20}>20 {t('brainDump.recommended')}</option>
            <option value={25}>25</option>
            <option value={30}>30</option>
          </select>
        </div>
        <label className="flex items-center gap-1.5 text-text-muted cursor-pointer">
          <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} className="accent-brand" />
          {t('brainDump.showAllLabel')}
        </label>
        <button
          onClick={() => setShowAdvanced(!showAdvanced)}
          className={cn(
            'px-3 py-1.5 rounded-lg border transition-all',
            showAdvanced
              ? 'bg-brand/10 border-brand text-brand'
              : 'bg-surface-2 border-border text-text-muted hover:text-text-primary hover:border-brand/50'
          )}
        >
          {showAdvanced ? t('brainDump.hideSettings') : t('brainDump.advanced')}
        </button>
      </div>

      {showAdvanced && (
        <div className="mb-4 grid grid-cols-1 md:grid-cols-2 gap-3 p-4 bg-surface-2 border border-border rounded-xl animate-fade-in">
          <TemplateSelector selectedTemplates={selectedTemplates} onSelect={setSelectedTemplates} />
          <WeightAdjuster />
        </div>
      )}

      {/* Input Area */}
      <div className="mb-6 sm:mb-8 lg:mb-[30px]">
        <div className="relative">
          <textarea
            value={textInput}
            onChange={(e) => setTextInput(e.target.value)}
            placeholder={t('brainDump.placeholder')}
            className="w-full min-h-[160px] sm:min-h-[200px] p-4 sm:p-5 pr-14 bg-surface-2 border border-border rounded-xl text-text-primary text-sm sm:text-[15px] resize-y font-[inherit] leading-[1.7] placeholder:text-text-faint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 focus-visible:border-brand/50 transition-all duration-200"
          />
          {/* Mic button */}
          <button
            onClick={isRecording ? handleStopRecording : handleStartRecording}
            disabled={isTranscribing}
            className={cn(
              'absolute bottom-3 right-3 w-10 h-10 rounded-full flex items-center justify-center transition-all',
              isRecording
                ? 'bg-danger text-white animate-pulse'
                : isTranscribing
                  ? 'bg-surface-1 text-text-muted cursor-wait'
                  : 'bg-surface-1 border border-border text-text-muted hover:text-brand hover:border-brand'
            )}
            title={isRecording ? t('brainDump.stopRecording') : isTranscribing ? t('brainDump.transcribing') : t('brainDump.voiceInput')}
          >
            {isTranscribing ? (
              <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
              </svg>
            ) : isRecording ? (
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="6" width="12" height="12" rx="2" /></svg>
            ) : (
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 1a4 4 0 0 0-4 4v6a4 4 0 0 0 8 0V5a4 4 0 0 0-4-4z"/>
                <path d="M19 11a1 1 0 1 0-2 0 5 5 0 0 1-10 0 1 1 0 1 0-2 0 7 7 0 0 0 6 6.93V21h-3a1 1 0 1 0 0 2h8a1 1 0 1 0 0-2h-3v-3.07A7 7 0 0 0 19 11z"/>
              </svg>
            )}
          </button>
          {/* Recording timer */}
          {isRecording && (
            <div className="absolute bottom-3 right-14 flex items-center gap-1.5 px-2 py-1 rounded-lg bg-danger/10 text-danger text-xs font-mono">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-danger animate-pulse" />
              {Math.floor(recordingTime / 60)}:{(recordingTime % 60).toString().padStart(2, '0')}
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2 mt-3">
          <QuickActions onSelectTemplate={(template) => setTextInput(template)} />
          <button
            onClick={() => setShowTweetSettings(!showTweetSettings)}
            className={cn(
              'px-3 py-1.5 text-sm rounded-lg border transition-all',
              showTweetSettings
                ? 'bg-brand/10 border-brand text-brand'
                : 'bg-surface-2 border-border text-text-muted hover:text-text-primary hover:border-brand/50'
            )}
          >
            {showTweetSettings ? t('brainDump.hideSettings') : t('tweetSettings.title')}
          </button>
        </div>
        {showTweetSettings && (
          <TweetSettings value={tweetSettings} onChange={setTweetSettings} className="mt-3 animate-fade-in" />
        )}

        <div className="flex flex-col sm:flex-row gap-2 sm:gap-[10px] mt-3 sm:mt-[15px]">
          <button
            onClick={handleGenerate}
            disabled={isDisabled}
            className={cn(
              'flex-1 py-3 sm:py-[14px] border-none rounded-3xl text-sm sm:text-base font-bold transition-all',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand',
              isDisabled ? 'bg-surface-3 text-text-faint cursor-not-allowed' : 'bg-brand text-white cursor-pointer hover:bg-brand-hover active:scale-[0.98] shadow-glow-brand'
            )}
          >
            {status === 'generating' ? (
              <span className="flex items-center justify-center gap-2">
                <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                {t('common.generating')}
              </span>
            ) : t('brainDump.generateTweets')}
          </button>

          <button
            onClick={() => { setTextInput(''); setTweetCandidates([]); setStatus('idle'); setError(null); }}
            className="py-3 sm:py-[14px] px-6 bg-surface-2 border border-border rounded-3xl text-text-secondary cursor-pointer text-sm sm:text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/50 hover:bg-surface-3 hover:text-text-primary transition-all duration-150"
          >
            {t('common.clear')}
          </button>
        </div>

        <button
          onClick={handleStrategyGenerate}
          disabled={isStrategyDisabled}
          className={cn(
            'w-full mt-3 sm:mt-4 py-3 sm:py-[14px] rounded-3xl font-bold text-sm sm:text-base transition-all border border-transparent',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand',
            isStrategyDisabled ? 'bg-surface-3 text-text-faint cursor-not-allowed' : 'bg-gradient-brand text-white hover:opacity-90 active:scale-[0.98] shadow-glow-accent'
          )}
        >
          {strategyStatus === 'generating' ? (
            <span className="flex items-center justify-center gap-2">
              <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              {t('brainDump.strategyGenerating')}
            </span>
          ) : t('brainDump.strategyButton')}
        </button>
        <p className="text-xs text-text-muted mt-2">{t('brainDump.strategyDesc')}</p>
      </div>

      {/* Progress */}
      {status === 'generating' && (
        <div className="mb-4 sm:mb-5 p-3 sm:p-4 bg-surface-2 border border-brand/40 rounded-xl">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-text-primary">{t('brainDump.generatingCandidates')}</span>
            <span className="text-xs text-text-muted tabular-nums">{mainProgress}%</span>
          </div>
          <div className="h-2 bg-border rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-brand via-accent to-accent transition-all duration-500" style={{ width: `${mainProgress}%` }} />
          </div>
          <p className="text-xs text-text-muted mt-2">{t('brainDump.progressDesc')}</p>
        </div>
      )}

      {strategyStatus === 'generating' && (
        <div className="mb-4 sm:mb-5 p-3 sm:p-4 bg-surface-2 border border-brand/40 rounded-xl">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-text-primary">{t('brainDump.strategyGenerating')}</span>
            <span className="text-xs text-text-muted tabular-nums">{strategyProgress}%</span>
          </div>
          <div className="h-2 bg-border rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-brand via-accent to-accent transition-all duration-500" style={{ width: `${strategyProgress}%` }} />
          </div>
          <p className="text-xs text-text-muted mt-2">{t('brainDump.strategyProgressDesc')}</p>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="p-4 sm:p-5 bg-surface-2 border-2 border-danger rounded-xl mb-4 sm:mb-[20px] animate-shake space-y-3">
          <p className="text-danger m-0 text-sm sm:text-base">{error}</p>
          <ul className="text-xs sm:text-sm text-danger/80 list-disc pl-5 space-y-1 m-0">
            <li>{t('brainDump.errorHints.api')}</li>
            <li>{t('brainDump.errorHints.input')}</li>
            <li>{t('brainDump.errorHints.wait')}</li>
          </ul>
          <div className="flex gap-2 flex-wrap">
            <button onClick={handleGenerate} className="px-3 py-2 rounded-lg bg-surface-2 border border-border text-text-primary text-sm hover:border-brand">
              {t('common.retry')}
            </button>
            <button onClick={() => setError(null)} className="px-3 py-2 rounded-lg border border-border text-text-muted text-sm hover:border-brand">
              {t('common.close')}
            </button>
          </div>
        </div>
      )}

      {strategyError && (
        <div className="p-4 sm:p-5 bg-surface-2 border-2 border-warning rounded-xl mb-4 sm:mb-[20px] space-y-3">
          <p className="text-warning m-0 text-sm sm:text-base">{strategyError}</p>
          <div className="flex gap-2 flex-wrap">
            <button onClick={handleStrategyGenerate} className="px-3 py-2 rounded-lg bg-surface-2 border border-border text-text-primary text-sm hover:border-brand">
              {t('common.retry')}
            </button>
            <button onClick={() => setStrategyError(null)} className="px-3 py-2 rounded-lg border border-border text-text-muted text-sm hover:border-brand">
              {t('common.close')}
            </button>
          </div>
        </div>
      )}

      {/* Strategy Result */}
      {strategyResult && (
        <div className="mb-8 sm:mb-[30px] p-4 sm:p-5 bg-surface-2 border border-brand/30 rounded-xl space-y-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[11px] uppercase tracking-[0.08em] text-text-muted mb-1">{t('brainDump.strategyLabel')}</p>
              <h3 className="text-lg sm:text-xl font-bold text-brand">{t('brainDump.strategySet')}</h3>
            </div>
            {strategyResult.thoughtLog && (
              <button onClick={() => setShowThoughtLog(!showThoughtLog)} className="text-xs sm:text-sm text-text-muted hover:text-brand transition-colors">
                {showThoughtLog ? t('brainDump.hideThoughtLog') : t('brainDump.showThoughtLog')}
              </button>
            )}
          </div>

          {showThoughtLog && strategyResult.thoughtLog && (
            <div className="p-3 bg-surface-1 border border-border rounded-lg text-sm text-text-primary whitespace-pre-wrap leading-relaxed">
              {strategyResult.thoughtLog}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Standard Tweet */}
            <div className="p-4 bg-surface-1 border border-border rounded-lg flex flex-col gap-2">
              <div className="flex items-center justify-between gap-2">
                <h4 className="text-sm font-bold text-text-primary">{t('brainDump.standardTweet')}</h4>
                <button
                  onClick={() => handleCopySection(strategyResult.tweet, 'strategy-tweet')}
                  className={cn('px-3 py-1 rounded-lg text-xs font-semibold transition-all border', copiedSection === 'strategy-tweet' ? 'bg-success/20 text-success border-success' : 'bg-surface-2 text-text-primary border-border hover:border-brand')}
                >
                  {copiedSection === 'strategy-tweet' ? `${t('common.copied')}` : `${t('common.copy')}`}
                </button>
              </div>
              <p className="text-sm text-text-primary whitespace-pre-wrap leading-relaxed">{strategyResult.tweet || '—'}</p>
            </div>

            {/* Short Thread */}
            <div className="p-4 bg-surface-1 border border-border rounded-lg flex flex-col gap-2">
              <div className="flex items-center justify-between gap-2">
                <h4 className="text-sm font-bold text-text-primary">{t('brainDump.shortThread')}</h4>
                <button
                  onClick={() => handleCopySection(strategyResult.thread.filter(Boolean).join('\n'), 'strategy-thread')}
                  className={cn('px-3 py-1 rounded-lg text-xs font-semibold transition-all border', copiedSection === 'strategy-thread' ? 'bg-success/20 text-success border-success' : 'bg-surface-2 text-text-primary border-border hover:border-brand')}
                >
                  {copiedSection === 'strategy-thread' ? `${t('common.copied')}` : `${t('common.copyAll')}`}
                </button>
              </div>
              <div className="space-y-2">
                {strategyResult.thread.map((item, idx) => (
                  <div key={idx} className="flex items-start gap-2">
                    <span className="text-[11px] font-semibold text-text-muted rounded bg-surface-2 px-2 py-1 border border-border">{idx + 1}/3</span>
                    <p className="text-sm text-text-primary whitespace-pre-wrap leading-relaxed mb-0">{item || '—'}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Teaser -> Answer */}
            <div className="p-4 bg-surface-1 border border-border rounded-lg flex flex-col gap-3">
              <div className="flex items-center justify-between gap-2">
                <h4 className="text-sm font-bold text-text-primary">{t('brainDump.teaserAnswer')}</h4>
                <span className="text-[11px] text-text-muted">2 posts</span>
              </div>
              <div className="space-y-3">
                <div className="flex items-start gap-2">
                  <span className="text-[11px] font-semibold text-text-muted rounded bg-surface-2 px-2 py-1 border border-border">{t('brainDump.teaserLabel')}</span>
                  <div className="flex-1 space-y-1">
                    <p className="text-sm text-text-primary whitespace-pre-wrap leading-relaxed mb-0">{strategyResult.teaserSet.teaser || '—'}</p>
                    <button onClick={() => handleCopySection(strategyResult.teaserSet.teaser, 'strategy-teaser')} className={cn('px-2 py-1 rounded-lg text-[11px] font-semibold transition-all border', copiedSection === 'strategy-teaser' ? 'bg-success/20 text-success border-success' : 'bg-surface-2 text-text-primary border-border hover:border-brand')}>
                      {copiedSection === 'strategy-teaser' ? `${t('common.copied')}` : `${t('common.copy')}`}
                    </button>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-[11px] font-semibold text-text-muted rounded bg-surface-2 px-2 py-1 border border-border">{t('brainDump.answerLabel')}</span>
                  <div className="flex-1 space-y-1">
                    <p className="text-sm text-text-primary whitespace-pre-wrap leading-relaxed mb-0">{strategyResult.teaserSet.answer || '—'}</p>
                    <button onClick={() => handleCopySection(strategyResult.teaserSet.answer, 'strategy-answer')} className={cn('px-2 py-1 rounded-lg text-[11px] font-semibold transition-all border', copiedSection === 'strategy-answer' ? 'bg-success/20 text-success border-success' : 'bg-surface-2 text-text-primary border-border hover:border-brand')}>
                      {copiedSection === 'strategy-answer' ? `${t('common.copied')}` : `${t('common.copy')}`}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Note Promo */}
            <div className="p-4 bg-surface-1 border border-border rounded-lg flex flex-col gap-3">
              <div className="flex items-center justify-between gap-2">
                <h4 className="text-sm font-bold text-text-primary">{t('brainDump.notePromo')}</h4>
                <span className="text-[11px] text-text-muted">A/B/C</span>
              </div>
              <div className="space-y-3">
                {([
                  { key: 'A', value: strategyResult.notePromo.patternA, id: 'pattern-a' },
                  { key: 'B', value: strategyResult.notePromo.patternB, id: 'pattern-b' },
                  { key: 'C', value: strategyResult.notePromo.patternC, id: 'pattern-c' },
                ] as const).map((item) => (
                  <div key={item.key} className="flex items-start gap-2">
                    <span className="text-[11px] font-semibold text-text-muted rounded bg-surface-2 px-2 py-1 border border-border">{item.key}</span>
                    <div className="flex-1 space-y-1">
                      <p className="text-sm text-text-primary whitespace-pre-wrap leading-relaxed mb-0">{item.value || '—'}</p>
                      <button onClick={() => handleCopySection(item.value, `note-${item.id}`)} className={cn('px-2 py-1 rounded-lg text-[11px] font-semibold transition-all border', copiedSection === `note-${item.id}` ? 'bg-success/20 text-success border-success' : 'bg-surface-2 text-text-primary border-border hover:border-brand')}>
                        {copiedSection === `note-${item.id}` ? `${t('common.copied')}` : `${t('common.copy')}`}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Results */}
      {tweetCandidates.length > 0 && (
        <div>
          <h2 className="text-xl sm:text-2xl mb-4 sm:mb-5 text-text-primary text-balance">
            {t('brainDump.results', { count: tweetCandidates.length })}
          </h2>
          <div className="flex flex-col gap-3 sm:gap-[15px]">
            {tweetCandidates.map((candidate, index) => {
              const rankColor = index === 0 ? 'text-warning' : index === 1 ? 'text-text-secondary' : index === 2 ? 'text-brand' : 'text-text-muted';

              return (
                <div key={index} className="p-4 sm:p-5 bg-surface-2 border border-border rounded-xl transition-all duration-200 hover:border-border-hover hover:bg-surface-3 hover:shadow-card-hover">
                  <div className="flex justify-between items-start mb-2 sm:mb-[10px]">
                    <span className={`text-sm font-bold ${rankColor}`}>#{index + 1}</span>
                    <div className="flex items-center gap-2 sm:gap-[10px]">
                      <button onClick={() => setEditingIndex(index)} className="bg-transparent border-none text-text-muted cursor-pointer text-sm p-1 hover:text-brand transition-colors" title={t('brainDump.edit')}><svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg></button>
                      <button onClick={() => { storageService.addSavedIdea(candidate.text); toast(t('common.savedToIdeas'), 'success'); }} className="bg-transparent border-none text-text-muted cursor-pointer text-sm p-1 hover:text-brand transition-colors" title={t('brainDump.saveToIdeas')}>{t('brainDump.saveToIdeas')}</button>
                      <span className={cn('text-xs sm:text-sm font-bold tabular-nums px-2 py-1 rounded', candidate.score >= 0.8 ? 'text-success bg-success/10' : candidate.score >= 0.6 ? 'text-brand bg-brand/10' : 'text-text-muted bg-text-muted/10')}>
                        {(candidate.score * 100).toFixed(0)}
                      </span>
                    </div>
                  </div>
                  <p className="text-sm sm:text-base leading-[1.6] mt-0 mb-3 sm:mb-[15px] text-text-primary text-pretty whitespace-pre-wrap">{candidate.text}</p>

                  <button onClick={() => setExpandedScoreIndex(expandedScoreIndex === index ? null : index)} className="w-full mb-3 text-left text-xs text-brand hover:underline">
                    {expandedScoreIndex === index ? `▼ ${t('brainDump.hideScoreDetails')}` : `▶ ${t('brainDump.showScoreDetails')}`}
                  </button>

                  {expandedScoreIndex === index ? (
                    <div className="mb-3 p-3 bg-surface-1 rounded-lg"><ScoreBreakdown breakdown={candidate.breakdown} /></div>
                  ) : (
                    <div className="flex flex-wrap gap-2 sm:gap-[15px] text-xs text-text-muted tabular-nums mb-3">
                      <span>{t('brainDump.freshness')}: {(candidate.breakdown.freshness * 100).toFixed(0)}%</span>
                      <span>{t('brainDump.specificity')}: {(candidate.breakdown.specificity * 100).toFixed(0)}%</span>
                      <span>{t('brainDump.personaFit')}: {(candidate.breakdown.personaFit * 100).toFixed(0)}%</span>
                      <span>{t('brainDump.replyHook')}: {(candidate.breakdown.replyHook * 100).toFixed(0)}%</span>
                      <span className="ml-auto">{candidate.text.length} {t('common.chars')}</span>
                      {candidate.templateType && <span className="text-accent">{candidate.templateType}</span>}
                    </div>
                  )}

                  <div className="flex gap-2">
                    <button
                      onClick={() => handleCopyTweet(candidate.text, index)}
                      className={cn('flex-1 py-2.5 px-4 rounded-lg font-medium transition-all', copiedIndex === index ? 'bg-success/20 text-success border border-success' : 'bg-brand/10 text-brand border border-brand/30 hover:bg-brand/20')}
                    >
                      {copiedIndex === index ? t('common.copied') : t('common.copy')}
                    </button>
                    <button
                      onClick={() => handlePostToX(candidate.text, toast, t)}
                      className="px-4 py-2.5 bg-surface-2 border border-border text-text-primary rounded-lg hover:bg-surface-1 transition-all font-medium"
                    >{t('common.postToX')}</button>
                    <button
                      onClick={() => { draftService.saveDraft(candidate.text, candidate.score, candidate.breakdown); toast(t('common.savedToDrafts'), 'success'); }}
                      className="px-4 py-2.5 bg-surface-2 border border-border text-text-primary rounded-lg hover:bg-surface-1 transition-all"
                      title={t('common.savedToDrafts')}
                    >{t('common.save')}</button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {editingIndex !== null && (
        <TweetEditor
          initialText={tweetCandidates[editingIndex].text}
          onSave={(newText) => handleEditSave(editingIndex, newText)}
          onCancel={() => setEditingIndex(null)}
        />
      )}
    </div>
  );
};
